import { Box, Button, Grid, Group, Tabs } from '@mantine/core';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { IconArrowRight, IconDeviceFloppy } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import SettingsTabGTM from '../../components/Settings/Tabs/Google/SettingaTabGTM';
import SettingsTabGCS from '../../components/Settings/Tabs/Google/SettingsTabGCS';
import SettingsTabGoogle from '../../components/Settings/Tabs/Google/SettingsTabGoogle';
import SettingsTabGoogleAnalytics from '../../components/Settings/Tabs/Google/SettingsTabGoogleAnalytics';
import { Page, SettingsDomainTabs } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';
import {
  setGoogleAccount as setGoogleAccountRedux,
  setGoogleAnalyticsAccount as setGoogleAnalyticsAccountRedux,
} from '../../store/googleSlice';

import { setDomain as setDomainRedux } from '../../store/domainSlice';

const SettingGooglePage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentGoogleAccount = useSelector((state) => state.google.account);
  const currentGoogleAnalyticsAccount = useSelector((state) => state.google.analyticsAccount);
  const { domain: currentDomain } = useSelector((state) => state.domain);

  const [googleAccount, setGoogleAccount] = useState(currentGoogleAccount);
  const [domain, setDomain] = useState(currentDomain);
  const [merchantCenters, setMerchantCenters] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [googleAnalyticsAccount, setGoogleAnalyticsAccount] = useState(currentGoogleAnalyticsAccount);
  const [hasGoogleAnalyticsAccounts, setHasGoogleAnalyticsAccounts] = useState(false);

  const search = queryString.parse(location.search);
  let tab = search.tab || 'google';
  const googleAccountChanged = JSON.stringify(currentGoogleAccount) !== JSON.stringify(googleAccount);
  const googleAnalyticsAccountChanged =
    JSON.stringify(currentGoogleAnalyticsAccount) !== JSON.stringify(googleAnalyticsAccount);

  useEffect(() => {
    if (domain?.id !== currentDomain?.id) {
      setDomain(currentDomain);
    }
  }, [domain?.id, currentDomain]);

  useEffect(() => {
    setGoogleAccount(currentGoogleAccount);
    setGoogleAnalyticsAccount(currentGoogleAnalyticsAccount);
  }, [currentGoogleAccount, currentGoogleAnalyticsAccount]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/google/account`)
      .then(({ googleAccount }) => {
        setGoogleAccount(googleAccount);
      })
      .catch(setError);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/googleAnalytics/account`)
      .then(({ googleAnalyticsAccount }) => {
        setGoogleAnalyticsAccount(googleAnalyticsAccount);
      })
      .catch(setError);
  }, [fetch, domain.accountId, domain.id]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/googleAnalytics/accounts`)
      .then(({ options }) => {
        setHasGoogleAnalyticsAccounts(!!options.length);
      })
      .catch(setError);
  }, [fetch, domain.accountId, domain.id, googleAccount?.adAccountId]);

  useEffect(() => {
    if (
      !googleAccount?.refreshTokenRevoked &&
      !googleAccount?.adAccountDisabled &&
      googleAccount?.adAccountId &&
      googleAccount?.domainId === domain.id
    ) {
      fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/google/${googleAccount.adAccountId}/customerConversionGoals`
      )
        .then(({ customerConversionGoals }) => {
          setGoogleAccount((prev) => ({
            ...prev,
            hasCustomerConversionGoals: customerConversionGoals?.results?.length > 0,
          }));
        })
        .catch(setError);

      fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/google/merchantCenterAccounts/${googleAccount.adAccountId}`
      )
        .then(({ options, requiredAssets }) => {
          if (options.length) {
            setMerchantCenters(options);
          }
          setGoogleAccount((prev) => ({ ...prev, requiredAssets }));
        })
        .catch(setError);
    }
  }, [
    fetch,
    domain.accountId,
    domain.id,
    googleAccount?.domainId,
    googleAccount?.adAccountId,
    googleAccount?.adAccountDisabled,
    googleAccount?.refreshTokenRevoked,
    googleAccount?.merchantId,
  ]);

  useEffect(() => {
    if (
      !googleAccount?.refreshTokenRevoked &&
      !googleAccount?.adAccountDisabled &&
      googleAccount?.adAccountId &&
      googleAccount?.domainId === domain.id &&
      googleAnalyticsAccount?.propertyId
    ) {
      fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/googleAnalytics/getGoogleAdsLink/${googleAccount.adAccountId}/${googleAnalyticsAccount.propertyId}`
      )
        .then(({ googleAdsLinkId, adsPersonalizationEnabled }) => {
          setGoogleAnalyticsAccount((prev) => ({
            ...prev,
            googleAdsLinkId,
            adsPersonalizationEnabled,
          }));
        })
        .catch(setError);
    }
  }, [
    fetch,
    domain.accountId,
    domain.id,
    googleAccount?.domainId,
    googleAccount?.adAccountId,
    googleAccount?.adAccountDisabled,
    googleAccount?.refreshTokenRevoked,
    googleAnalyticsAccount?.propertyId,
  ]);

  const merchantCenter = googleAccount?.merchantId
    ? merchantCenters.find((c) => c.id === googleAccount.merchantId)
    : null;

  const hasProducts = Boolean(merchantCenter?.hasProducts);

  const setTab = (tab) => {
    navigate(location.pathname.replace(`/settings/google`, `/settings/google?tab=${tab}`));
  };

  const validateForm = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    return true;
  };

  const save = async (account) => {
    setError(undefined);
    try {
      const { googleAccount } = await fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/google/account`,
        {
          method: 'PUT',
          body: JSON.stringify(account),
        }
      );
      dispatch(setGoogleAccountRedux(googleAccount));
    } catch (error) {
      setError(error);
      // Rethrow so callers (e.g. the wizard flow in submitForm) can stop
      // advancing to the next step when the save failed.
      throw error;
    }
  };

  const manageGoogleAnalyticsAccount = async (account, options = { isUpdate: false }) => {
    setError(undefined);
    try {
      const { isUpdate } = options;
      const method = isUpdate ? 'PUT' : 'POST';
      const url = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/googleAnalytics/account${
        isUpdate ? `/${account?.id}` : ''
      }`;

      const { googleAnalyticsAccount } = await fetch(url, {
        method,
        body: JSON.stringify(account),
      });
      dispatch(setGoogleAnalyticsAccountRedux(googleAnalyticsAccount));
    } catch (error) {
      setError(error);
      // Rethrow so the wizard flow in submitForm can abort the auto-advance.
      throw error;
    }
  };

  const updateAndCreateAccounts = async (googleAccount, googleAnalyticsAccount) => {
    dispatch(setGoogleAnalyticsAccountRedux(googleAnalyticsAccount));

    // Skip the Ads PUT when nothing on that tab changed. The wizard "Next"
    // button is enabled even with no changes (so users can advance from a
    // saved Ads tab to the GA tab) — that previously fired a redundant PUT.
    if (googleAccountChanged) {
      await save(googleAccount);
    }

    // Only POST/PUT the GA account when there's actually something to save.
    // Without these guards, saving the page (e.g., to persist a Merchant
    // Center link) always tries to upsert the GA row too — and Sequelize
    // rejects with "Google Analytics Account.account Id cannot be null"
    // when the user hasn't filled in any GA fields.
    const hasAnyGaFields =
      googleAnalyticsAccount?.accountId || googleAnalyticsAccount?.propertyId || googleAnalyticsAccount?.dataStreamId;
    const hasGaFields =
      googleAnalyticsAccount?.accountId && googleAnalyticsAccount?.propertyId && googleAnalyticsAccount?.dataStreamId;
    const isUpdate = !!googleAnalyticsAccount?.id;
    const shouldSaveGa = hasGoogleAnalyticsAccounts && googleAnalyticsAccountChanged && (isUpdate || hasGaFields);

    // Surface partial GA input as a client-side error instead of silently
    // dropping the save — otherwise the user thinks the save succeeded.
    if (hasGoogleAnalyticsAccounts && googleAnalyticsAccountChanged && !isUpdate && hasAnyGaFields && !hasGaFields) {
      const validationError = new Error(
        'Please fill in Account ID, Property ID, and Data Stream ID to save the Google Analytics account.'
      );
      setError(validationError);
      throw validationError;
    }

    if (shouldSaveGa) {
      await manageGoogleAnalyticsAccount(
        {
          ...googleAnalyticsAccount,
          googleAccountId: googleAccount.id,
        },
        { isUpdate }
      );
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'adAccountId') {
      setGoogleAccount((prev) => ({ ...prev, [name]: value, merchantId: null }));
    } else {
      setGoogleAccount((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleChangeAnalytics = (event) => {
    const { name, value } = event.target;
    setGoogleAnalyticsAccount((prev) => ({ ...prev, [name]: value }));
  };

  const connectMerchantCenter = async (customerId, merchantId) => {
    try {
      const { productLinkId } = await fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/google/connectMerchantCenter/`,
        {
          method: 'POST',
          body: JSON.stringify({ customerId, merchantId }),
        }
      );

      if (!productLinkId) {
        setError(new Error('Merchant Center connect succeeded but no productLinkId was returned. Please retry.'));
        return;
      }
      setGoogleAccount((prev) => ({ ...prev, productLinkId }));
    } catch (err) {
      setError(err);
      return;
    }

    // Refresh the merchant-center list in a separate try/catch — a failure
    // here must not surface as an error banner, since the connect itself
    // already succeeded. The useEffect above will refetch on next load.
    try {
      const { options } = await fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/google/merchantCenterAccounts/${customerId}`
      );
      if (options?.length) {
        setMerchantCenters(options);
      }
    } catch {
      // intentionally swallowed — connect already succeeded
    }
  };

  const saveDomain = () => {
    return fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        gtmId: domain.gtmId,
      }),
    })
      .then(({ productLinkId }) => {
        setGoogleAccount((prev) => ({ ...prev, productLinkId }));
      })
      .catch(setError);
  };

  const linkGA4Property = (customerId, propertyId) => {
    return fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/googleAnalytics/createGoogleAdsLink`, {
      method: 'POST',
      body: JSON.stringify({
        customerId,
        propertyId,
      }),
    })
      .then(({ googleAdsLinkId, adsPersonalizationEnabled }) => {
        setGoogleAnalyticsAccount((prev) => ({
          ...prev,
          googleAdsLinkId,
          adsPersonalizationEnabled,
        }));
      })
      .catch(setError);
  };

  const isWizardNextStep = tab === 'google' && hasGoogleAnalyticsAccounts && !googleAnalyticsAccount?.id;

  const submitForm = async (e) => {
    e.preventDefault();
    setError(undefined);
    setSaving(true);

    if (tab === 'gtm') {
      saveDomain()
        .then(() => {
          fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}`, {
            method: 'GET',
          }).then(({ domain: fullDomain }) => {
            dispatch(setDomainRedux(fullDomain));
          });
        })
        .catch(setError)
        .finally(() => setSaving(false));
    } else if (validateForm(e)) {
      try {
        await updateAndCreateAccounts(googleAccount, googleAnalyticsAccount);

        if (isWizardNextStep) {
          setTab('googleAnalytics');
        }
      } catch (error) {
        setError(error);
      } finally {
        setSaving(false);
      }
    }
  };

  const connect = (e) => {
    e.preventDefault();
    setConnecting(true);
    connectMerchantCenter(googleAccount.adAccountId, googleAccount.merchantId).finally(() => setConnecting(false));
  };

  const link = (e) => {
    e.preventDefault();
    setLinking(true);
    linkGA4Property(googleAccount.adAccountId, googleAnalyticsAccount.propertyId).finally(() => setLinking(false));
  };

  let connectButton;
  if (googleAccount?.merchantId) {
    connectButton = (
      <Button disabled={merchantCenter?.linked || connecting} onClick={connect}>
        {merchantCenter?.linked || connecting ? (connecting ? 'Connecting' : 'Connected') : 'Connect'}
      </Button>
    );
  }

  let linkButton;
  if (googleAccount?.adAccountId && googleAnalyticsAccount?.propertyId) {
    linkButton = (
      <>
        <Button fullWidth disabled={googleAnalyticsAccount?.googleAdsLinkId || linking} onClick={link}>
          {googleAnalyticsAccount?.googleAdsLinkId || linking ? (linking ? 'Linking' : 'Linked') : 'Link'}
        </Button>
      </>
    );
  }

  const gtmValid = domain.gtmId?.startsWith('GTM-') || domain.gtmId?.startsWith('G-');

  // Merchant validation must always block save, even in wizard mode. Keep
  // it separate from the "no changes" condition so the wizard can override
  // only the latter (it intentionally lets users advance without changes).
  const merchantInvalid =
    !!googleAccount?.merchantId && !googleAccount?.productSync && hasProducts === false;
  const noChanges = !googleAccountChanged && !googleAnalyticsAccountChanged;

  let saveDisabled = noChanges || merchantInvalid; //||
  //(hasGoogleAnalyticsAccounts && !googleAnalyticsAccount?.googleAdsLinkId); // TODO: should maybe not be acondition??
  if (tab === 'gtm') {
    saveDisabled = domain.gtmId && !gtmValid;
  }

  let saved = !googleAccountChanged && !googleAnalyticsAccountChanged;

  if (tab === 'gtm') {
    saved = currentDomain.gtmId === domain.gtmId;
  }

  const buttonDisabled = saving || merchantInvalid || (!isWizardNextStep && (saveDisabled || saved));

  let buttonLabel;
  if (saving) {
    buttonLabel = 'Saving...';
  } else if (isWizardNextStep) {
    buttonLabel = saved ? 'Next' : 'Save & Next';
  } else {
    buttonLabel = saved ? 'Saved' : 'Save';
  }

  const ButtonIcon = isWizardNextStep && saved ? IconArrowRight : IconDeviceFloppy;

  const saveButton = (
    <Button type="submit" disabled={buttonDisabled}>
      <ButtonIcon size={14} /> {buttonLabel}
    </Button>
  );

  return (
    <div>
      <Metadata title={`${Page['settings']} | ${SettingsDomainTabs[tab]}`} />

      <Grid pt={30} my={20}>
        <Grid.Col span={12}>
          <Box component="form" ta="left" noValidate onSubmit={(e) => submitForm(e)}>
            <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
              <Tabs.List>
                <Tabs.Tab value="google">Google Ads</Tabs.Tab>
                {hasGoogleAnalyticsAccounts && (
                  <Tabs.Tab
                    value="googleAnalytics"
                    disabled={!googleAccount?.adAccountId || !hasGoogleAnalyticsAccounts}
                  >
                    Google Analytics
                  </Tabs.Tab>
                )}
                <Tabs.Tab value="gcs">Google Cloud Storage</Tabs.Tab>
                <Tabs.Tab value="gtm">Google Tag Manager</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="google">
                <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
                  <SettingsTabGoogle
                    googleAccount={googleAccount}
                    setGoogleAccount={setGoogleAccount}
                    handleChange={handleChange}
                    connectButton={connectButton}
                    merchantCenter={merchantCenter}
                  />
                </GoogleOAuthProvider>
              </Tabs.Panel>
              {hasGoogleAnalyticsAccounts && (
                <Tabs.Panel value="googleAnalytics">
                  <SettingsTabGoogleAnalytics
                    googleAnalyticsAccount={googleAnalyticsAccount}
                    handleChange={handleChangeAnalytics}
                    linkButton={linkButton}
                  />
                </Tabs.Panel>
              )}
              <Tabs.Panel value="gcs">
                <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
                  <SettingsTabGCS />
                </GoogleOAuthProvider>
              </Tabs.Panel>
              <Tabs.Panel value="gtm">
                <SettingsTabGTM
                  domain={domain}
                  handleChange={(e) => setDomain({ ...domain, [e.target.name]: e.target.value })}
                  gtmValid={gtmValid}
                />
              </Tabs.Panel>
            </Tabs>
            {tab !== 'gcs' && (
              <Group justify="flex-end" gap="xs" py="md">
                {error && <ErrorMessage message={error.message} />}
                {saveButton}
              </Group>
            )}
          </Box>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default SettingGooglePage;
